import { useState } from 'react';
import { useGT } from 'gt-react';
import { toast } from 'src/components/ui/toast';
import {
  useOverrideWindowState,
  type OverrideWindowPayload,
} from '../../../replenishment/components/useOverrideWindowState';
import {
  parseInlineValue,
  type InlineOverrideField,
} from './inlineOverrideField';

/** What the dirty check compares: the window and reason as typed. */
function windowSnapshot(w: {
  startsAt: string;
  endsAt: string;
  reviewAt: string;
  reason: string;
}): string {
  return JSON.stringify([w.startsAt, w.endsAt, w.reviewAt, w.reason.trim()]);
}

/** A start after `now` schedules the override instead of applying it now. */
function windowStartsLater(
  window: Pick<OverrideWindowPayload, 'startsAt'>,
  now: Date,
): boolean {
  return (
    window.startsAt !== undefined && Date.parse(window.startsAt) > now.getTime()
  );
}

/**
 * The draft behind one inline override editor: the typed value, its window and
 * reason, whether it can be saved, and the save itself.
 *
 * Shared by the table's row editor and the popover on the summary cards, so
 * both validate, seed and write identically. Call it from a component that is
 * mounted only while the editor is open: it seeds from the governing override
 * on first render and starts clean every time it reopens.
 */
export function useInlineOverrideDraft(
  field: InlineOverrideField,
  onSaved: () => void,
) {
  const gt = useGT();
  const { override } = field;
  const [raw, setRaw] = useState(override ? String(override.value) : '');
  // An in-place replacement keeps the governing row's start, so a month-grain
  // factor keeps covering the buckets it already did; a new row starts now. A
  // review date already past is dropped, or the replacement is born overdue.
  const windowState = useOverrideWindowState(
    field.grain,
    override
      ? {
          startsAt: field.replaces ? override.startsAt : null,
          endsAt: override.endsAt,
          reviewAt: override.needsReview ? null : override.reviewAt,
          reason: override.reason,
        }
      : undefined,
  );
  const [seeded] = useState(() => windowSnapshot(windowState));
  const startsLater = windowStartsLater(windowState.payload, new Date());

  const value = parseInlineValue(raw, field.unit);
  const problem = value === null ? null : field.validate(value);
  const unchanged =
    override !== null &&
    value === override.value &&
    windowSnapshot(windowState) === seeded;
  const canSave =
    value !== null &&
    problem === null &&
    !unchanged &&
    windowState.isComplete &&
    windowState.orderValid &&
    !field.pending;

  async function save() {
    if (!canSave || value === null) return;
    try {
      await field.save({ value, window: windowState.payload, startsLater });
      toast.success(
        gt('{field} override saved', {
          context: 'inline override saved toast',
          field: field.label,
        }),
      );
      onSaved();
    } catch (e) {
      toast.error(
        gt('Failed to save {field} override: {msg}', {
          context: 'inline override error toast',
          field: field.label,
          msg: e instanceof Error ? e.message : String(e),
        }),
      );
    }
  }

  return { raw, setRaw, problem, canSave, windowState, startsLater, save };
}
